import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, AlertOctagon, Phone, MapPin, RotateCcw, Sparkles, Save, Mail, Loader2, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FORMSPREE_ID = 'mvkpjgoe';

// Quiz stages for multi-step funnel
const quizStages = [
  { name: 'Sleep Quality', questions: [1, 2, 3], color: 'cyan' },
  { name: 'Physical Symptoms', questions: [4, 5, 6], color: 'blue' },
  { name: 'Health Factors', questions: [7, 8], color: 'purple' },
  { name: 'Daily Impact', questions: [9, 10], color: 'amber' },
];

const questions = [
  {
    id: 1,
    question: "Do you snore loudly (loud enough to be heard through closed doors or your partner complains)?",
    options: [
      { text: "Never", score: 0 },
      { text: "Rarely", score: 1 },
      { text: "Sometimes", score: 2 },
      { text: "Frequently / Every night", score: 3 }
    ]
  },
  {
    id: 2,
    question: "Has anyone observed you stop breathing or gasp during sleep?",
    options: [
      { text: "Never", score: 0 },
      { text: "Rarely", score: 1 },
      { text: "Sometimes", score: 2 },
      { text: "Frequently", score: 3 }
    ]
  },
  {
    id: 3,
    question: "Do you feel excessively tired, fatigued, or sleepy during the day?",
    options: [
      { text: "Never", score: 0 },
      { text: "Rarely", score: 1 },
      { text: "Sometimes", score: 2 },
      { text: "Almost every day", score: 3 }
    ]
  },
  {
    id: 4,
    question: "Do you wake up with headaches in the morning?",
    options: [
      { text: "Never", score: 0 },
      { text: "Rarely", score: 1 },
      { text: "A few times a week", score: 2 },
      { text: "Almost every morning", score: 3 }
    ]
  },
  {
    id: 5,
    question: "Do you have difficulty concentrating or experience memory problems?",
    options: [
      { text: "No issues", score: 0 },
      { text: "Occasionally", score: 1 },
      { text: "Frequently", score: 2 },
      { text: "Severely affects my daily life", score: 3 }
    ]
  },
  {
    id: 6,
    question: "Do you wake up with a dry mouth or sore throat?",
    options: [
      { text: "Never", score: 0 },
      { text: "Rarely", score: 1 },
      { text: "Several times a week", score: 2 },
      { text: "Every morning", score: 3 }
    ]
  },
  {
    id: 7,
    question: "Have you been diagnosed with or treated for high blood pressure?",
    options: [
      { text: "No", score: 0 },
      { text: "Borderline / Pre-hypertension", score: 1 },
      { text: "Yes, controlled with medication", score: 2 },
      { text: "Yes, difficult to control", score: 3 }
    ]
  },
  {
    id: 8,
    question: "What is your neck circumference?",
    options: [
      { text: "Less than 15 inches", score: 0 },
      { text: "15-16 inches", score: 1 },
      { text: "16-17 inches", score: 2 },
      { text: "More than 17 inches / Not sure", score: 3 }
    ]
  },
  {
    id: 9,
    question: "Do you feel irritable, moody, or depressed without a clear reason?",
    options: [
      { text: "Never", score: 0 },
      { text: "Occasionally", score: 1 },
      { text: "Frequently", score: 2 },
      { text: "Most of the time", score: 3 }
    ]
  },
  {
    id: 10,
    question: "Have you ever nodded off or fallen asleep while driving or at a stoplight?",
    options: [
      { text: "Never", score: 0 },
      { text: "Once or twice", score: 1 },
      { text: "Several times", score: 2 },
      { text: "Frequently", score: 3 }
    ]
  }
];

const getResult = (score) => {
  if (score <= 7) {
    return {
      level: 'low',
      title: 'Low Risk',
      color: 'green',
      icon: CheckCircle,
      description: 'Based on your responses, your risk for sleep apnea appears to be low. However, if you snore regularly or your partner has concerns about your breathing during sleep, a professional evaluation is still recommended.',
      recommendation: 'Continue practicing good sleep hygiene. If symptoms develop or worsen, schedule a consultation with Dr. Yang.'
    };
  } else if (score <= 17) {
    return {
      level: 'moderate',
      title: 'Moderate Risk',
      color: 'amber',
      icon: AlertTriangle,
      description: 'Your responses suggest a moderate risk for obstructive sleep apnea. Several of your symptoms are commonly associated with sleep-disordered breathing. A professional airway evaluation is recommended.',
      recommendation: 'We strongly recommend scheduling a consultation with Dr. Maichou Yang for a comprehensive airway evaluation. Early treatment can prevent serious health complications.'
    };
  } else {
    return {
      level: 'high',
      title: 'High Risk',
      color: 'red',
      icon: AlertOctagon,
      description: 'Your responses indicate a high risk for obstructive sleep apnea. Multiple significant symptoms are present, and untreated sleep apnea can lead to serious cardiovascular, metabolic, and cognitive health consequences.',
      recommendation: 'Please schedule an appointment with Dr. Maichou Yang as soon as possible. A sleep study and comprehensive airway evaluation are strongly recommended to protect your health.'
    };
  }
};

const SleepApneaQuiz = () => {
  const [searchParams] = useSearchParams();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [gateData, setGateData] = useState({ firstName: '', email: '' });
  const [selectedOption, setSelectedOption] = useState(null);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [prefilledQuestions, setPrefilledQuestions] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveEmail, setSaveEmail] = useState('');
  const [savingProgress, setSavingProgress] = useState(false);
  const [progressSaved, setProgressSaved] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [exitIntentDismissed, setExitIntentDismissed] = useState(false);
  const [exitIntentEmail, setExitIntentEmail] = useState('');
  const [exitIntentSubmitting, setExitIntentSubmitting] = useState(false);
  const [exitIntentSubmitted, setExitIntentSubmitted] = useState(false);

  // Parse prefill params and apply them (for both symptom prefill and resume links)
  useEffect(() => {
    const qParams = searchParams.getAll('q');
    const resumeParam = searchParams.get('resume');
    
    if (resumeParam) {
      // Resume from saved progress
      try {
        const decoded = JSON.parse(atob(resumeParam));
        if (decoded.answers) {
          setAnswers(decoded.answers);
          setCurrentQuestion(decoded.currentQuestion || 0);
          setPrefillApplied(true);
          setPrefilledQuestions(Object.keys(decoded.answers).map(Number));
        }
      } catch (e) {
        console.error('Failed to parse resume data');
      }
    } else if (qParams.length > 0) {
      const prefilled = {};
      const prefilledIds = [];
      qParams.forEach(param => {
        const [questionId, score] = param.split(':').map(Number);
        if (questionId && !isNaN(score)) {
          prefilled[questionId] = score;
          prefilledIds.push(questionId);
        }
      });
      if (Object.keys(prefilled).length > 0) {
        setAnswers(prefilled);
        setPrefilledQuestions(prefilledIds);
        setPrefillApplied(true);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Sleep Apnea Risk Quiz | Sunshine Dental Care Roseville CA';
  }, []);

  // Exit intent detection - show special offer when user tries to leave mid-quiz
  useEffect(() => {
    const handleMouseLeave = (e) => {
      // Only trigger if mouse leaves through top of viewport
      if (e.clientY <= 0) {
        // Only show if: quiz in progress, not at results, not already dismissed, and answered at least 1 question
        const quizInProgress = !showResult && !showGate && Object.keys(answers).length > 0 && Object.keys(answers).length < questions.length;
        if (quizInProgress && !exitIntentDismissed && !showExitIntent && !showSaveModal) {
          setShowExitIntent(true);
          // Track exit intent event
          if (window.gtag) {
            window.gtag('event', 'quiz_exit_intent_triggered', {
              'event_category': 'Sleep Apnea Quiz',
              'event_label': `Question ${currentQuestion + 1}`,
              'value': currentQuestion + 1
            });
          }
        }
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [showResult, showGate, answers, exitIntentDismissed, showExitIntent, showSaveModal, currentQuestion]);

  // Mobile back-button intercept - detect when user tries to navigate back mid-quiz
  useEffect(() => {
    const quizInProgress = !showResult && !showGate && Object.keys(answers).length > 0 && Object.keys(answers).length < questions.length;
    
    // Only set up back button intercept if quiz is in progress
    if (!quizInProgress) return;

    // Push a state to history so we can detect back button
    const stateKey = 'quiz-exit-intercept';
    if (!window.history.state || window.history.state.key !== stateKey) {
      window.history.pushState({ key: stateKey }, '');
    }

    const handlePopState = (e) => {
      // User pressed back button
      if (!exitIntentDismissed && !showExitIntent && !showSaveModal) {
        // Push state again to prevent actual navigation
        window.history.pushState({ key: stateKey }, '');
        setShowExitIntent(true);
        
        // Track mobile exit intent
        if (window.gtag) {
          window.gtag('event', 'quiz_exit_intent_triggered', {
            'event_category': 'Sleep Apnea Quiz',
            'event_label': `Mobile Back Button - Question ${currentQuestion + 1}`,
            'value': currentQuestion + 1,
            'device_type': 'mobile'
          });
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showResult, showGate, answers, exitIntentDismissed, showExitIntent, showSaveModal, currentQuestion]);

  // Save quiz progress to localStorage for retargeting
  useEffect(() => {
    if (Object.keys(answers).length > 0 && !showResult) {
      const progressData = {
        answers,
        currentQuestion,
        timestamp: Date.now(),
        completed: false
      };
      localStorage.setItem('quizProgress', JSON.stringify(progressData));
    }
  }, [answers, currentQuestion, showResult]);

  // Mark quiz as completed when results are shown
  useEffect(() => {
    if (showResult) {
      localStorage.removeItem('quizProgress'); // Clear incomplete progress
      localStorage.setItem('quizCompleted', Date.now().toString());
    }
  }, [showResult]);

  // Handle exit intent form submission
  const handleExitIntentSubmit = async (e) => {
    e.preventDefault();
    setExitIntentSubmitting(true);
    const resumeLink = generateResumeLink();
    
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: 'Sleep Apnea Quiz - Exit Intent Save',
          'Lead Type': 'Quiz Exit Intent Capture',
          'Email': exitIntentEmail,
          'Progress': `Question ${currentQuestion + 1} of ${questions.length}`,
          'Resume Link': resumeLink,
        }),
      });
      // Track successful capture
      if (window.gtag) {
        window.gtag('event', 'quiz_exit_intent_captured', {
          'event_category': 'Sleep Apnea Quiz',
          'event_label': 'Email Captured',
        });
      }
    } catch {}
    setExitIntentSubmitting(false);
    setExitIntentSubmitted(true);
  };

  const dismissExitIntent = () => {
    setShowExitIntent(false);
    setExitIntentDismissed(true);
  };

  // Allow user to actually leave after confirming
  const handleLeaveQuiz = () => {
    setExitIntentDismissed(true);
    setShowExitIntent(false);
    // Navigate back - go to homepage or previous page
    window.history.go(-2); // Go back 2 steps (past our pushed state)
  };

  // Generate resume link
  const generateResumeLink = () => {
    const data = { answers, currentQuestion };
    const encoded = btoa(JSON.stringify(data));
    return `${window.location.origin}/sleep-apnea-quiz?resume=${encoded}`;
  };

  // Save progress handler
  const handleSaveProgress = async (e) => {
    e.preventDefault();
    setSavingProgress(true);
    const resumeLink = generateResumeLink();
    
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: 'Sleep Apnea Quiz - Save Progress',
          'Lead Type': 'Quiz Progress Save',
          'Email': saveEmail,
          'Progress': `Question ${currentQuestion + 1} of ${questions.length}`,
          'Resume Link': resumeLink,
          'Message': `Click here to resume your Sleep Apnea Risk Quiz: ${resumeLink}`,
        }),
      });
      setProgressSaved(true);
    } catch {
      setProgressSaved(true);
    } finally {
      setSavingProgress(false);
    }
  };

  const handleSelect = (questionId, score) => {
    setSelectedOption(score);
    setAnswers({ ...answers, [questionId]: score });

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
      } else {
        setShowGate(true);
      }
    }, 400);
  };

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    setGateSubmitting(true);
    const score = Object.values({ ...answers, [questions[questions.length - 1].id]: selectedOption || 0 }).reduce((s, v) => s + v, 0);
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: 'Sleep Apnea Quiz Lead',
          'Lead Type': 'Sleep Apnea Quiz Completion',
          'First Name': gateData.firstName,
          'Email': gateData.email,
          'Quiz Score': `${score} / ${questions.length * 3}`,
          'Risk Level': getResult(score).title,
        }),
      });
    } catch {}
    setGateSubmitting(false);
    setShowGate(false);
    setShowResult(true);
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(answers[questions[currentQuestion - 1].id] ?? null);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setShowGate(false);
    setGateData({ firstName: '', email: '' });
    setSelectedOption(null);
    setPrefillApplied(false);
    setPrefilledQuestions([]);
    setShowSaveModal(false);
    setSaveEmail('');
    setProgressSaved(false);
    window.scrollTo(0, 0);
  };

  const totalScore = Object.values(answers).reduce((sum, val) => sum + val, 0);
  const result = getResult(totalScore);
  const progress = ((currentQuestion + (showResult ? 1 : 0)) / questions.length) * 100;

  const ResultIcon = result.icon;

  const colorMap = {
    green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: 'text-green-500', badge: 'bg-green-100 text-green-800' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-800' },
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-500', badge: 'bg-red-100 text-red-800' }
  };

  const colors = colorMap[result.color];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="sleep-apnea-quiz-page">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-cyan-700 to-cyan-900 text-white pt-32 pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-testid="quiz-title">
            Do I Have Sleep Apnea?
          </h1>
          <p className="text-cyan-100 text-lg max-w-2xl mx-auto">
            Take this 2-minute self-assessment to evaluate your risk for obstructive sleep apnea. 
            Answer honestly — your results are private and not stored.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Pre-fill banner */}
        {prefillApplied && !showResult && !showGate && (
          <div className="mb-6 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4 flex items-start gap-3" data-testid="prefill-banner">
            <Sparkles size={20} className="text-cyan-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-cyan-800 font-medium text-sm">
                We've pre-filled {prefilledQuestions.length} question{prefilledQuestions.length > 1 ? 's' : ''} based on your symptoms
              </p>
              <p className="text-cyan-600 text-xs mt-1">
                Review each answer and adjust if needed. Pre-filled answers are marked.
              </p>
            </div>
          </div>
        )}

        {/* Stage Indicators */}
        {!showResult && !showGate && (
          <div className="mb-6">
            <div className="flex justify-between items-center gap-2">
              {quizStages.map((stage, idx) => {
                const stageQuestions = stage.questions;
                const currentQuestionId = questions[currentQuestion].id;
                const isCurrentStage = stageQuestions.includes(currentQuestionId);
                const isCompleted = stageQuestions.every(qId => answers[qId] !== undefined);
                const isPast = idx < quizStages.findIndex(s => s.questions.includes(currentQuestionId));
                
                return (
                  <div key={idx} className="flex-1">
                    <div className={`text-xs font-medium mb-1 text-center truncate ${
                      isCurrentStage ? 'text-cyan-700' : isPast || isCompleted ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {stage.name}
                    </div>
                    <div className={`h-2 rounded-full transition-all ${
                      isCurrentStage ? 'bg-cyan-500' : isPast || isCompleted ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>{showResult ? 'Complete' : `Question ${currentQuestion + 1} of ${questions.length}`}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-cyan-600 h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="quiz-progress-bar"
            />
          </div>
        </div>

        {!showResult && !showGate ? (
          /* Question Card */
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="quiz-question-card">
            {/* Prefill indicator */}
            {prefillApplied && prefilledQuestions.includes(questions[currentQuestion].id) && (
              <div className="flex items-center gap-2 text-cyan-600 text-sm mb-4 bg-cyan-50 px-3 py-2 rounded-lg" data-testid="prefill-indicator">
                <Sparkles size={16} />
                <span>Pre-filled based on your symptoms. Feel free to adjust.</span>
              </div>
            )}
            
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-8">
              {questions[currentQuestion].question}
            </h2>

            <div className="space-y-3">
              {questions[currentQuestion].options.map((option, idx) => {
                const isCurrentAnswer = answers[questions[currentQuestion].id] === option.score;
                const isPrefilled = prefilledQuestions.includes(questions[currentQuestion].id) && isCurrentAnswer;
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelect(questions[currentQuestion].id, option.score)}
                    data-testid={`quiz-option-${idx}`}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      selectedOption === option.score || isCurrentAnswer
                        ? isPrefilled
                          ? 'border-cyan-500 bg-cyan-50 text-cyan-800 ring-2 ring-cyan-200'
                          : 'border-cyan-500 bg-cyan-50 text-cyan-800'
                        : 'border-gray-200 hover:border-cyan-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className="font-medium">{option.text}</span>
                    {isPrefilled && (
                      <span className="ml-2 text-xs text-cyan-500">(from symptoms)</span>
                    )}
                  </button>
                );
              })}
            </div>

            {currentQuestion > 0 && (
              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
                  data-testid="quiz-back-button"
                >
                  <ArrowLeft size={16} />
                  Previous question
                </button>
                
                {/* Save Progress Button */}
                {Object.keys(answers).length > 0 && (
                  <button
                    onClick={() => setShowSaveModal(true)}
                    className="inline-flex items-center gap-2 text-gray-400 hover:text-cyan-600 text-sm transition-colors"
                    data-testid="save-progress-button"
                  >
                    <Save size={16} />
                    Save & finish later
                  </button>
                )}
              </div>
            )}
            
            {/* Save Progress Button for first question */}
            {currentQuestion === 0 && Object.keys(answers).length > 0 && (
              <div className="mt-6 text-right">
                <button
                  onClick={() => setShowSaveModal(true)}
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-cyan-600 text-sm transition-colors"
                  data-testid="save-progress-button-first"
                >
                  <Save size={16} />
                  Save & finish later
                </button>
              </div>
            )}
          </div>
        ) : showGate && !showResult ? (
          /* Email Gate */
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 text-center" data-testid="quiz-gate">
            <CheckCircle size={40} className="mx-auto text-cyan-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Results Are Ready!</h2>
            <p className="text-gray-500 mb-6">Enter your name and email to see your personalized sleep apnea risk assessment.</p>
            <form onSubmit={handleGateSubmit} className="max-w-sm mx-auto space-y-4">
              <input
                type="text"
                required
                placeholder="First name"
                value={gateData.firstName}
                onChange={(e) => setGateData({ ...gateData, firstName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                data-testid="quiz-gate-firstname"
              />
              <input
                type="email"
                required
                placeholder="Email address"
                value={gateData.email}
                onChange={(e) => setGateData({ ...gateData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                data-testid="quiz-gate-email"
              />
              <button
                type="submit"
                disabled={gateSubmitting}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-bold py-3 rounded-lg transition-colors"
                data-testid="quiz-gate-submit"
              >
                {gateSubmitting ? 'Loading...' : 'See My Results'}
              </button>
            </form>
            <p className="text-xs text-gray-400 mt-4">We respect your privacy. No spam, ever.</p>
          </div>
        ) : (
          /* Results */
          <div data-testid="quiz-results">
            {/* Result Card */}
            <div className={`${colors.bg} ${colors.border} border-2 rounded-2xl p-8 mb-8`}>
              <div className="flex items-center gap-3 mb-4">
                <ResultIcon size={32} className={colors.icon} />
                <div>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${colors.badge}`}>
                    {result.title}
                  </span>
                  <p className="text-gray-500 text-sm mt-1">Score: {totalScore} / {questions.length * 3}</p>
                </div>
              </div>
              <p className={`${colors.text} text-lg mb-4`}>{result.description}</p>
              <p className="text-gray-700 font-medium">{result.recommendation}</p>
            </div>

            {/* CTA */}
            <div className="bg-cyan-600 rounded-2xl p-8 text-white text-center mb-8">
              <h3 className="text-2xl font-bold mb-3">Schedule Your Airway Evaluation</h3>
              <p className="text-cyan-100 mb-6 max-w-lg mx-auto">
                Dr. Maichou Yang can perform a comprehensive airway assessment and recommend the right treatment plan for you.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="tel:9167731122"
                  className="inline-flex items-center gap-2 bg-white text-cyan-600 font-semibold px-6 py-3 rounded-lg hover:bg-cyan-50 transition-colors"
                  data-testid="quiz-call-button"
                >
                  <Phone size={18} />
                  Call (916) 773-1122
                </a>
                <a
                  href="https://www.google.com/maps/place/700+Sunrise+Ave+%23E,+Roseville,+CA+95661"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border-2 border-white text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <MapPin size={18} />
                  Get Directions
                </a>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleRestart}
                className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold transition-colors"
                data-testid="quiz-restart-button"
              >
                <RotateCcw size={18} />
                Retake Quiz
              </button>
              <Link
                to="/#blog"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 font-semibold transition-colors"
              >
                <ArrowRight size={18} />
                Read Our Sleep Apnea Articles
              </Link>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-4 bg-gray-100 rounded-lg text-gray-500 text-xs text-center">
              <strong>Disclaimer:</strong> This quiz is for informational purposes only and is not a substitute for professional medical diagnosis. 
              Only a qualified healthcare provider can diagnose obstructive sleep apnea through a proper sleep study. 
              If you suspect you have sleep apnea, please consult Dr. Maichou Yang at Sunshine Dental Care or your primary care physician.
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* Save Progress Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="save-progress-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowSaveModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in fade-in zoom-in duration-300">
            {!progressSaved ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Save size={28} className="text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Save Your Progress</h3>
                  <p className="text-gray-500 text-sm">
                    We'll email you a link to resume exactly where you left off 
                    (Question {currentQuestion + 1} of {questions.length}).
                  </p>
                </div>
                
                <form onSubmit={handleSaveProgress} className="space-y-4">
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      required
                      placeholder="Your email address"
                      value={saveEmail}
                      onChange={(e) => setSaveEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                      data-testid="save-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={savingProgress}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="save-submit-button"
                  >
                    {savingProgress ? (
                      <><Loader2 size={18} className="animate-spin" /> Sending...</>
                    ) : (
                      <>
                        <Mail size={18} />
                        Email Resume Link
                      </>
                    )}
                  </button>
                </form>
                
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="mt-4 w-full text-gray-500 hover:text-gray-700 text-sm transition-colors"
                >
                  Continue quiz instead
                </button>
              </>
            ) : (
              <div className="text-center" data-testid="save-success">
                <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Link Sent!</h3>
                <p className="text-gray-600 mb-4">
                  Check your inbox at <span className="font-medium text-gray-900">{saveEmail}</span> for a link to resume your quiz anytime.
                </p>
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors"
                >
                  Continue Quiz
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Exit Intent Popup */}
      {showExitIntent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="exit-intent-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismissExitIntent} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* Close button */}
            <button
              onClick={dismissExitIntent}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
              data-testid="exit-intent-close"
            >
              <X size={24} />
            </button>

            {!exitIntentSubmitted ? (
              <>
                {/* Gradient header */}
                <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 px-8 py-6 text-white text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Wait! You're Almost There</h3>
                  <p className="text-cyan-100 text-sm">
                    You're {Math.round(((currentQuestion + 1) / questions.length) * 100)}% through your assessment
                  </p>
                </div>

                {/* Content */}
                <div className="p-8">
                  <p className="text-gray-600 text-center mb-6">
                    Don't lose your progress! Enter your email and we'll send you a link to finish your sleep apnea risk assessment later.
                  </p>

                  <form onSubmit={handleExitIntentSubmit} className="space-y-4">
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        required
                        placeholder="Your email address"
                        value={exitIntentEmail}
                        onChange={(e) => setExitIntentEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                        data-testid="exit-intent-email"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={exitIntentSubmitting}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
                      data-testid="exit-intent-submit"
                    >
                      {exitIntentSubmitting ? (
                        <><Loader2 size={18} className="animate-spin" /> Sending...</>
                      ) : (
                        <>
                          <Mail size={18} />
                          Email Me a Resume Link
                        </>
                      )}
                    </button>
                  </form>

                  <button
                    onClick={dismissExitIntent}
                    className="mt-4 w-full text-gray-400 hover:text-gray-600 text-sm transition-colors"
                  >
                    No thanks, I'll continue the quiz
                  </button>
                  
                  <button
                    onClick={handleLeaveQuiz}
                    className="mt-2 w-full text-gray-300 hover:text-gray-500 text-xs transition-colors"
                    data-testid="exit-intent-leave"
                  >
                    Leave quiz without saving
                  </button>
                </div>
              </>
            ) : (
              <div className="p-8 text-center" data-testid="exit-intent-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Link Sent!</h3>
                <p className="text-gray-600 mb-4">
                  Check your inbox at <span className="font-medium text-gray-900">{exitIntentEmail}</span> for a link to resume your quiz anytime.
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => { setShowExitIntent(false); setExitIntentDismissed(true); }}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-3 rounded-lg transition-colors"
                  >
                    Continue My Quiz Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SleepApneaQuiz;
